<template>
  <FilterOverview :showFilterInitially="false" :title="title" @reload="onReload">
    <template #toolbar-left>
      <div class="d-flex align-items-center justify-content-between flex-wrap flex-sm-nowrap">
        <RegionFilterButtonGroup
          :regions="['EU', 'FERN']"
          :disabled="regionFilterDisabled"
          v-model="selectedRegions"
        ></RegionFilterButtonGroup>

        <!-- Filter Reset Button -->
        <div
          v-if="isFilterSupplied"
          class="icon-button d-flex justify-content-center text-danger mr-4 ml-2"
          style="width: 36px; min-width: 36px"
          @click="resetAllFilters"
          v-b-tooltip.hover.bottom
          title="Filter zurücksetzen"
        >
          <span class="d-flex align-items-center"><FunnelX :size="20" /></span>
        </div>
      </div>
    </template>
    <template #table="{ tableHeight }">
      <AgGridVue
        :style="{ height: tableHeight + 87 + 'px' }"
        class="ag-theme-alpine m-0 p-0"
        :columnDefs="columnDefs"
        :defaultColDef="defaultColDef"
        :rowModelType="'serverSide'"
        :getRowHeight="getRowHeight"
        :statusBar="statusBar"
        :enableCellTextSelection="true"
        suppressContextMenu
        suppressRowTransform
        :tooltipShowDelay="tooltipShowDelay"
        :tooltipMouseTrack="true"
        @grid-ready="onGridReady"
        @rowDoubleClicked="onRowDoubleClicked"
        suppressCellFocus
      ></AgGridVue>
      <AufgabeErstellenModal
        @aufgabeErstellt="onAufgabeErstellt"
        :prefixedVerlinkungen="aufgabenVerlinkung"
      ></AufgabeErstellenModal>
    </template>
  </FilterOverview>
</template>
<script>
import FilterOverview from '@/components/common/filter-overview.vue';
import { differenceInDays, parseISO, add } from 'date-fns';
import CustomTooltip from '@/components/flugverfuegbarkeit/custom-tooltip.vue';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-alpine.css';
import { AgGridVue } from 'ag-grid-vue';
import 'ag-grid-enterprise';
import AufgabeErstellenModal from '@/components/aufgaben/aufgabe-erstellen-modal.vue';
import StatusBarComponent from '@/components/flugverfuegbarkeit/status-bar-ag-grid.vue';
import * as odataService from '@/core/common/services/odata.service';
import CellRendererPax from '@/components/produkte/reisetermine/cell-renderer-pax.vue';
import CellRendererAufgaben from '@/components/produkte/reisetermine/cell-renderer-aufgaben.vue';
import CellRendererPrioritaet from '@/components/flugverfuegbarkeit/cell-renderer-prioritaet.vue';
import CellRendererZimmerkontingente from '@/components/common/zimmerkontingente/cell-renderer-zimmerkontingente.vue';
import { formatDateWithoutHours, vorzeitigGarantiertClass } from '@/core/common/helpers/utils';
import CustomHeader from '@/components/flugverfuegbarkeit/custom-header.vue';
import AgDateRangePicker from '@/components/flugverfuegbarkeit/ag-date-range-picker.vue';
import { mapState, mapGetters } from 'vuex';
import { fernRegions, euRegions } from '@/core/common/helpers/utils.js';
import RegionFilterButtonGroup from '@/components/common/region-filter-button-group.vue';
import CustomInfoIconHeader from '@/components/common/custom-info-icon-header.vue';
import { FunnelX } from 'lucide-vue';

export default {
  components: {
    FilterOverview,
    AgGridVue,
    CustomTooltip,
    StatusBarComponent,
    CellRendererAufgaben,
    CellRendererPax,
    CellRendererPrioritaet,
    CellRendererZimmerkontingente,
    CustomHeader,
    AgDateRangePicker,
    AufgabeErstellenModal,
    RegionFilterButtonGroup,
    CustomInfoIconHeader: CustomInfoIconHeader,
    FunnelX,
  },

  data() {
    return {
      title: 'ZVC-Übersicht',
      isFilterSupplied: false,
      columnDefs: null,
      tooltipShowDelay: 0,
      regionFilterSelected: null,
      regionFilterDisabled: true,
      statusBar: null,
      gridApi: null,
      columnApi: null,
      prefixedVerlinkungen: [],
      count: null,
      selectedRegions: [],
      expand: {
        reise: {},
        pax: {},
        aufgaben: { orderBy: ['faelligkeitsdatum'] },
        metadata: {},
        zimmerkontingente: {},
      },
      defaultColDef: {
        floatingFilter: true,
        filter: true,
        flex: 1,
        resizable: false,
        sortable: false,
        suppressMenu: true,
        floatingFilterComponentParams: {
          suppressFilterButton: true,
        },
        suppressMovable: true,
        filterParams: {
          defaultToNothingSelected: true,
        },
        tooltipComponent: 'CustomTooltip',
      },
    };
  },
  watch: {
    // schaut auf das array "selectedRegions" und dessen veränderung
    selectedRegions(newSelectedRegions) {
      // und geben das newSelectedRegions aus dem emit in die methode
      this.changeRegionFilterInstance(newSelectedRegions);
    },
  },
  computed: {
    ...mapState({
      aufgabenVerlinkung: state => state.zvc.aufgabenVerlinkung,
    }),
    ...mapGetters(['getKategorienDestinationYield', 'getReisekuerzelOptions', 'getKategorienLabel']),
    reisenOptions() {
      return this.getReisekuerzelOptions.map(r => r.reisekuerzel);
    },
    // gibt als label den Lesbaren und als value den internen Wert zurück
    getAufgabenKategorienLabels() {
      return this.getKategorienDestinationYield.map(kategorie => ({
        label: this.getKategorienLabel(kategorie),
        value: kategorie,
      }));
    },
    // es wird eine Zuordnung erstellt, die die Labels mit den internen Werten "verbindet"
    aufgabenLabelToValueMap() {
      const map = {};
      this.getAufgabenKategorienLabels.forEach(item => {
        map[item.label] = item.value;
      });
      return map;
    },
  },
  created() {
    this.statusBar = {
      statusPanels: [{ statusPanel: 'StatusBarComponent', key: 'statusBarCompKey', align: 'left' }],
    };
    this.columnDefs = [
      {
        field: 'istZusatztermin',
        headerName: '',
        resizable: false,
        sortable: false,
        floatingFilter: true,
        cellRenderer: params => {
          if (params.value) {
            return 'Z';
          }
          return '';
        },
        filterParams: {
          values: [true, false],
          valueFormatter: params =>
            params.value === true
              ? 'Zusatztermin'
              : params.value === false
              ? 'Kein Zusatztermin'
              : params.value,
        },
        width: 60,
        minWidth: 60,
      },
      {
        field: 'reise.reisekuerzel',
        headerName: 'Reisekürzel',
        width: 120,
        minWidth: 120,
        headerComponent: 'CustomHeader',
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          values: params => params.success(this.reisenOptions),
          debounceMs: 1000,
        },
        floatingFilter: true,
        sortable: true,
      },
      {
        field: 'reise.prioritaet',
        headerName: 'Priorität',
        sortable: true,
        width: 120,
        minWidth: 120,
        filterParams: {
          values: [0, 1, 2, 3],
          debounceMs: 1000,
          cellRenderer: 'CellRendererPrioritaet',
          cellRendererParams: { isFilterRenderer: true },
        },
        floatingFilter: true,
        cellRenderer: 'CellRendererPrioritaet',
        headerComponent: 'CustomHeader',
      },

      {
        field: 'startdatum',
        headerName: 'Startdatum',
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        sortable: true,
        filter: 'AgDateRangePicker',
        valueFormatter: params => formatDateWithoutHours(params.value),
        minWidth: 160,
        maxWidth: 185,
        floatingFilter: true,
      },
      {
        field: 'garantiert',
        headerName: 'Gar.',
        width: 70,
        minWidth: 70,
        cellRenderer: params => {
          const { value } = params;
          if (value) {
            return "<i class='fas fa-check text-success fa-lg'/></span>";
          } else {
            return "<i class='ml-1 fas fa-times text-danger fa-lg'/>";
          }
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          values: [true, false],
          cellRenderer: params => {
            if (params.value === '(Select All)') {
              return params.value;
            } else if (params.value) {
              return "<i class='fas fa-check text-success fa-lg'/>";
            } else if (!params.value) {
              return "<i class='ml-1 fas fa-times text-danger fa-lg'/>";
            }
          },
          debounceMs: 1000,
        },
      },
      {
        field: 'vorzeitigGarantiert',
        headerName: 'Vorz. gar.',
        minWidth: 110,
        cellRenderer: params => {
          const iconClass = this.vorzeitigGarantiertClass(params.value);
          return `<i class="${iconClass}"></i>`;
        },
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['VorzeitigGarantiert', 'WarVorzeitigGarantiert', 'NichtVorzeitigGarantiert'],
          valueFormatter: params =>
            params.value === 'VorzeitigGarantiert'
              ? 'Vorzeitig garantiert'
              : params.value === 'WarVorzeitigGarantiert'
              ? 'War vorzeitig garantiert'
              : params.value === 'NichtVorzeitigGarantiert'
              ? 'Nicht vorzeitig garantiert'
              : params.value,
          debounceMs: 1000,
        },
      },
      {
        field: 'releasedatum',
        headerName: 'Release (TbR)',
        headerTooltip: 'Tage bis Release',
        filter: 'AgDateRangePicker',
        floatingFilter: true,
        floatingFilterComponent: 'AgDateRangePicker',
        floatingFilterComponentParams: {
          isChild: true,
        },
        minWidth: 190,
        width: 190,
        cellRenderer: params => {
          const daysUntilRelease = differenceInDays(parseISO(params.value), new Date());
          return formatDateWithoutHours(params.value) + ' (' + daysUntilRelease + ')';
        },
        headerComponent: 'CustomHeader',
        sortable: true,
      },
      {
        headerName: 'Zimmerkontingente',
        field: 'zimmerkontingente',
        cellRenderer: 'CellRendererZimmerkontingente',
        width: 200,
        minWidth: 200,
        cellRendererParams: { isZvc: true },
        cellStyle: {
          'line-height': 'normal',
          display: 'flex',
          'align-items': 'center',
          'justify-items': 'center',
          overflow: 'visible',
        },
        filter: false,
      },
      {
        field: 'pax',
        headerName: 'PAX',
        headerComponent: 'CustomInfoIconHeader',
        headerComponentParams: {
          hoverText: 'Zum Filtern kann eine Zahl, eine Range oder </> für die Suche eingegeben werden.',
        },
        cellRenderer: 'CellRendererPax',
        filter: 'agTextColumnFilter',
        filterParams: {
          buttons: ['apply'],
        },
        minWidth: 170,
        width: 170,
        suppressRowTransform: true,
        cellStyle: {
          'line-height': 'normal',
          display: 'flex',
          'justify-items': 'center',
          overflow: 'visible',
        },
        cellRendererParams: params => ({
          paxObject: params.data.pax,
          minPax: params.data.metadata.minPax,
          maxPax: params.data.metadata.maxPax,
          kalkPax: params.data.metadata.kalkPax,
          forecastPax: params.data.metadata.forecastPax,
          durchfuehrbareMinPax: params.data.metadata.durchfuehrbareMinPax,
          id: params.data.id,
        }),
      },
      {
        field: 'erwartetePaxMinusIstPax',
        headerName: 'Noch FC PAX',
        filter: 'agSetColumnFilter',
        filterParams: {
          values: ['> Verf. Zimmerkapazität', '<= Verf. Zimmerkapazität'],
          cellRenderer: params => {
            if (params.value !== null) {
              if (params.value === '> Verf. Zimmerkapazität') {
                return '<span style="font-size: 13px; display:flex; align-items:center">> Verf. Zimmerkapazität <span class="badge badge-danger ml-1" style="width: 16px; height: 16px"> </span></span>';
              } else if (params.value === '<= Verf. Zimmerkapazität') {
                return '<span style="font-size: 13px; display:flex; align-items:center"><= Verf. Zimmerkapazität <span class="badge badge-success ml-1" style="width: 16px; height: 16px"> </span></span>';
              } else return '(Select all)';
            }
          },
        },
        minWidth: 160,
        width: 160,
        cellRenderer: params => {
          const { zimmerkontingentePersonenFrei, nochForecastPax } = params.data;
          // Grün wenn die noch erwarteten Forecast-Pax in die freien Zimmerplätze passen, sonst rot
          const colorClass = nochForecastPax <= zimmerkontingentePersonenFrei ? 'green' : 'red';
          return `<span class="kachel-badge ${colorClass}">${nochForecastPax}</span>`;
        },
      },
      {
        field: 'maxPaxMinusIstPax',
        headerName: 'PAX bis Max',
        minWidth: 150,
        width: 150,
        filterParams: {
          values: ['> Verf. Zimmerkapazität'],
          cellRenderer: params => {
            if (params.value !== null) {
              if (params.value === '> Verf. Zimmerkapazität') {
                return '<span style="font-size: 13px; display:flex; align-items:center">> Verf. Zimmerkapazität <span class="badge badge-warning ml-1" style="width: 16px; height: 16px"> </span></span>';
              } else return '(Select all)';
            }
          },
        },
        cellRenderer: params => {
          const { paxBisMax, zimmerkontingentePersonenFrei } = params.data;
          // Gelb als Warnung wenn mehr Pax bis Max als freie Zimmerplätze, sonst neutral grau
          const colorClass = paxBisMax > zimmerkontingentePersonenFrei ? 'yellow' : 'gray';
          return `<span class="kachel-badge ${colorClass}">${paxBisMax}</span>`;
        },
      },
      {
        field: 'aufgaben',
        headerName: 'Aufgaben',
        cellRenderer: 'CellRendererAufgaben',
        filter: 'agSetColumnFilter',
        filterParams: {
          refreshValuesOnOpen: true,
          values: params => {
            const labels = this.getAufgabenKategorienLabels.map(item => item.label);
            params.success(labels);
          },
          debounceMs: 500,
        },
        cellRendererParams: params => ({
          data: params.data,
          filter: 'zvc-overview',
          ansicht: 'zvc-overview',
          onPlusSignClicked: () => {
            this.$bvModal.show('modalAufgabeEdit');
          },
        }),
        cellStyle: {
          'line-height': 'normal',
          display: 'flex',
          'align-items': 'center',
        },
        minWidth: 550,
        width: 550,
      },
      {
        field: 'reise.region',
        headerName: 'Region',
        filter: 'agSetColumnFilter',
        width: 100,
        minWidth: 100,
        filterParams: {
          values: ['EU', 'Fern'],
          debounceMs: 1000,
        },
        cellRenderer: params => {
          if (euRegions.includes(params.value)) {
            return 'EU';
          } else if (fernRegions.includes(params.value)) {
            return 'Fern';
          } else return '-';
        },
        hide: true,
      },
    ];
  },
  methods: {
    vorzeitigGarantiertClass,
    getRowHeight(params) {
      const amountZimmerkontingente = params.data?.zimmerkontingente?.filter(
        z => z.isDeleted === false
      ).length;
      const amountZvcAufgaben = params.data?.aufgaben?.filter(aufgabe =>
        this.getKategorienDestinationYield.includes(aufgabe.kategorie)
      ).length;
      const maxZimmerkontingente = Math.max(Math.ceil(amountZimmerkontingente / 4) * 45, 45);
      const maxAufgaben = Math.max(1, amountZvcAufgaben) * 45;
      return params.data ? Math.max(maxZimmerkontingente, maxAufgaben) : 45;
    },
    updateStatusBar(displayedRowsCount) {
      const statusBarComponent = this.gridApi?.getStatusPanel('statusBarCompKey');
      statusBarComponent?.setRowCount(displayedRowsCount);
      statusBarComponent?.setOdataCount(this.count);
    },
    onGridReady(params) {
      this.gridApi = params.api;
      this.gridColumnApi = params.columnApi;

      const updateData = () => {
        const server = this.server();
        const datasource = this.createDatasource(server);
        params.api.setServerSideDatasource(datasource);
      };
      updateData();
    },
    createDatasource(server) {
      return {
        getRows: async params => {
          const response = await server.getData(params.request);
          if (response.success) {
            params.success({
              rowData: response.rows,
            });
            this.updateStatusBar(this.gridApi?.getDisplayedRowCount());
          } else {
            params.fail();
          }
        },
      };
    },
    server() {
      return {
        getData: async request => {
          console.log('[Datasource] - rows requested by grid: ', request);
          const response = await odataService.getReisetermin({
            filter: this.getOdataFilterFromAgGridRequest(request),
            top: request.endRow - request.startRow,
            skip: request.startRow,
            count: true,
            expand: this.expand,
            ...(request.sortModel.length > 0
              ? {
                  orderBy: request.sortModel.map(s => s.colId.replaceAll('.', '/') + ' ' + s.sort),
                }
              : { orderBy: 'reise/prioritaet asc, reise/reisekuerzel asc, startdatum asc' }),
          });
          this.count = response.count;
          this.regionFilterDisabled = false;
          return {
            success: true,
            rows: response.data,
          };
        },
      };
    },
    getOdataFilterFromAgGridRequest(request) {
      this.checkIfFilterSupplied();

      const filterKeys = Object.keys(request.filterModel);
      let filters = [];

      const allFilterKeys = filterKeys.map(key => {
        const formattedKey = key.replaceAll('.', '/');
        if (request.filterModel[key].filterType === 'text') {
          if (formattedKey === 'pax') {
            if (request.filterModel[key].filter.includes('-')) {
              const min = parseInt(request.filterModel[key].filter.split('-')[0]);
              const max = parseInt(request.filterModel[key].filter.split('-')[1]);
              return { 'pax/ist': { le: max, ge: min } };
            } else if (request.filterModel[key].filter[0] === '>') {
              const min = parseInt(request.filterModel[key].filter.split('>')[1]);
              return { 'pax/ist': { gt: min } };
            } else if (request.filterModel[key].filter[0] === '<') {
              const max = parseInt(request.filterModel[key].filter.split('<')[1]);
              return { 'pax/ist': { lt: max } };
            } else return { 'pax/ist': parseInt(request.filterModel[key].filter) };
          }
          return { [formattedKey]: { startswith: request.filterModel[key].filter } };
        } else if (request.filterModel[key].filterType === 'set') {
          if (key === 'reise.region') {
            const regionValues = request.filterModel[key].values
              .map(value => (value === 'Fern' ? fernRegions : euRegions))
              .flat();
            return { [formattedKey]: { in: regionValues } };
          }
          if (formattedKey === 'erwartetePaxMinusIstPax') {
            if (request.filterModel[key].values.length === 1) {
              if (request.filterModel[key].values.includes('> Verf. Zimmerkapazität')) {
                return {
                  ['nochForecastPax']: {
                    gt: { type: 'guid', value: 'zimmerkontingentePersonenFrei' },
                  },
                };
              } else {
                return {
                  ['nochForecastPax']: {
                    le: { type: 'guid', value: 'zimmerkontingentePersonenFrei' },
                  },
                };
              }
            } else return;
          }
          if (formattedKey === 'maxPaxMinusIstPax') {
            if (request.filterModel[key].values.length === 1) {
              if (request.filterModel[key].values.includes('> Verf. Zimmerkapazität')) {
                return {
                  ['paxBisMax']: {
                    gt: { type: 'guid', value: 'zimmerkontingentePersonenFrei' },
                  },
                };
              }
            }
          }
          if (formattedKey === 'aufgaben') {
            // die lesbaren Werte werden in die internen Werte konvertiert
            const values = request.filterModel[key].values.map(label => this.aufgabenLabelToValueMap[label]);
            // Gib mir alle Aufgaben, bei denen die Kategorie (a/kategorie) gleich (eq) diesem speziellen Wert (value) ist.
            // und wenn eine Zeile noch eine weitere Aufgabe hat
            const categoriesFilter = values.map(value => `aufgabe/kategorie eq '${value}'`).join(' or ');
            // O-Data Abfrage, damit nur die Zeilen mit den richtigen Lables angezeigt werden
            return `aufgaben/any(aufgabe: ${categoriesFilter})`;
          } else {
            return { [formattedKey]: { in: request.filterModel[key].values } };
          }
        } else if (request.filterModel[key].filterType === 'date') {
          const dateFrom = new Date(request.filterModel[key].dateFrom.substring(0, 10));
          const dateTo = add(new Date(request.filterModel[key].dateTo), { hours: 23, minutes: 59 });
          if (formattedKey === 'versandReiseunterlagen') {
            return {
              unterlagenversandDatum: {
                ge: dateFrom,
                le: dateTo,
              },
            };
          } else {
            return {
              [formattedKey]: {
                ge: dateFrom,
                le: dateTo,
              },
            };
          }
        } else if (request.filterModel[key].filterType === 'boolean') {
          return { [formattedKey]: { eq: true } };
        }
      });

      filters = [
        ...filters,
        { state: 'Aufgelegt', isDeleted: false, abgesagt: false, releasedatum: { ge: new Date() } },
        ...allFilterKeys,
      ];
      return filters;
    },

    onRowDoubleClicked($event) {
      const { data, rowIndex } = $event;
      this.selectedRowID = rowIndex;
      const routeData = this.$router.resolve({ path: '/reisetermine/' + data.id });
      window.open(routeData.href, '_blank');
    },
    resetAllFilters() {
      this.gridApi.setFilterModel(null);
      let filterDate1 = this.gridApi.getFilterInstance('startdatum');
      let filterDate2 = this.gridApi.getFilterInstance('releasedatum');
      filterDate1.onDateRangeFilterChanged(null);
      filterDate2.onDateRangeFilterChanged(null);
      this.selectedRegions = [];
    },
    checkIfFilterSupplied() {
      this.isFilterSupplied = this.gridApi && Object.keys(this.gridApi?.getFilterModel()).length > 0;
    },
    onAufgabeErstellt() {
      this.gridApi?.deselectAll();
      this.gridApi.onFilterChanged();
    },
    resetPrefixedVerlinkungen() {
      this.prefixedVerlinkungen = [];
    },
    onReload() {
      this.gridApi.onFilterChanged();
    },
    changeRegionFilterInstance(selectedRegions) {
      if (!this.gridApi) return;

      const regionsFilter = this.gridApi.getFilterInstance('reise.region');
      if (!regionsFilter) return;

      if (selectedRegions.length > 0) {
        regionsFilter.setModel({ values: selectedRegions });
      } else {
        regionsFilter.setModel({});
      }
      this.gridApi.onFilterChanged();
    },
  },
};
</script>
<style lang="scss" scoped>
@import '~ag-grid-community/styles/ag-grid.css';
@import '~ag-grid-community/styles/ag-theme-alpine.css';
:deep(.ag-theme-alpine) {
  --ag-font-family: 'Poppins';
  --ag-font-size: 13px;
  --ag-tab-min-width: 300px;
}
:deep(.ag-theme-alpine .ag-header-cell) {
  font-weight: 500;
  font-size: 14px;
}
:deep(.ag-theme-alpine .ag-header-group-cell) {
  font-weight: 500;
  font-size: 14px;
}
:deep(.ag-cell[col-id='reisetermin.istZusatztermin']) {
  padding-left: 0px !important;
  border: none;
}
:deep(.ag-header-cell[aria-colindex='1']) {
  padding-left: 4px;
  padding-right: 4px;
}

.icon-button {
  height: 32.52px;
}
</style>
